import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Typography, Row, Col, Card, Statistic, Table, DatePicker, Select, Spin, Empty } from 'antd';
import { TeamOutlined, ClockCircleOutlined, ThunderboltOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

import ListPageHeader from '@shared/ListPageHeader';
import workforceStatsService from '@services/operator/workforceStatsService';

const { RangePicker } = DatePicker;

const STATION_OPTIONS = ['picking', 'packing', 'inbound', 'stock_taking'];

const STATION_COLORS = {
  picking: '#1890ff',
  packing: '#52c41a',
  inbound: '#faad14',
  returns: '#f5222d',
  stock_taking: '#722ed1',
};

const DailySummary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState([dayjs().subtract(7, 'day'), dayjs()]);
  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [selectedStation, setSelectedStation] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const from = dateRange[0].format('YYYY-MM-DD');
      const to = dateRange[1].format('YYYY-MM-DD');

      const [summaryRes, operatorsRes] = await Promise.all([
        workforceStatsService.getDailySummary(from, to),
        workforceStatsService.getOperatorsOverview(selectedDate, selectedStation),
      ]);

      if (summaryRes.success) setSummaryData(summaryRes.data);
      if (operatorsRes.success) setOperators(operatorsRes.data.operators || []);
    } catch (error) {
      console.error('[WorkforceStats] Fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedDate, selectedStation]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 找出所有出現過的站別
  const activeStations = useMemo(() => {
    if (!summaryData?.days) return [];
    const set = new Set();
    summaryData.days.forEach((day) => day.stations.forEach((s) => set.add(s.station)));
    return Array.from(set);
  }, [summaryData]);

  // UPH 趨勢圖資料
  const uphTrendData = useMemo(() => {
    if (!summaryData?.days) return [];
    return summaryData.days.map((day) => {
      const row = { date: day.date };
      activeStations.forEach((st) => {
        const stat = day.stations.find((s) => s.station === st);
        row[`uph_${st}`] = stat?.avg_uph || 0;
      });
      return row;
    });
  }, [summaryData, activeStations]);

  // 每日產出趨勢圖資料
  const outputTrendData = useMemo(() => {
    if (!summaryData?.days) return [];
    return summaryData.days.map((day) => {
      const row = { date: day.date };
      activeStations.forEach((st) => {
        const stat = day.stations.find((s) => s.station === st);
        row[`items_${st}`] = stat?.completed_items || 0;
      });
      return row;
    });
  }, [summaryData, activeStations]);

  // 選中日期的站別卡片
  const todayStations = useMemo(() => {
    if (!summaryData?.days) return [];
    const day = summaryData.days.find((d) => d.date === selectedDate);
    return day?.stations || [];
  }, [summaryData, selectedDate]);

  // 站別統計卡片
  const renderStationCards = () => {
    if (!todayStations.length) {
      return <Empty description={t('operator.workforce_stats.no_data')} />;
    }

    return (
      <Row gutter={[16, 16]}>
        {todayStations.map((station) => (
          <Col xs={24} sm={12} lg={6} key={station.station}>
            <Card
              title={t(`operator.workforce_stats.stations.${station.station}`)}
              size="small"
            >
              <Row gutter={[8, 12]}>
                <Col span={12}>
                  <Statistic
                    title={t('operator.workforce_stats.operators_count')}
                    value={station.operators_count}
                    prefix={<TeamOutlined />}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title={t('operator.workforce_stats.total_work_hours')}
                    value={station.total_work_hours}
                    suffix="h"
                    precision={1}
                    prefix={<ClockCircleOutlined />}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title={t('operator.workforce_stats.completed_tasks')}
                    value={station.completed_tasks}
                    prefix={<CheckCircleOutlined />}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title={t('operator.workforce_stats.avg_uph')}
                    value={station.avg_uph}
                    precision={1}
                    prefix={<ThunderboltOutlined />}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title={t('operator.workforce_stats.completed_items')}
                    value={station.completed_items}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title={t('operator.workforce_stats.best_uph')}
                    value={station.best_uph}
                    precision={1}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  // 全員績效表格
  const operatorColumns = [
    {
      title: t('operator.workforce_stats.operator_name'),
      dataIndex: 'operator_name',
      key: 'operator_name',
      fixed: 'left',
      width: 120,
    },
    {
      title: t('operator.workforce_stats.station'),
      key: 'station',
      width: 100,
      render: (_, record) =>
        record.stations.map((s) => t(`operator.workforce_stats.stations.${s.station}`)).join(', '),
    },
    {
      title: t('operator.workforce_stats.tasks_completed'),
      key: 'tasks_completed',
      width: 100,
      sorter: (a, b) => sumField(a, 'tasks_completed') - sumField(b, 'tasks_completed'),
      render: (_, record) => sumField(record, 'tasks_completed'),
    },
    {
      title: t('operator.workforce_stats.lines_processed'),
      key: 'lines_processed',
      width: 100,
      sorter: (a, b) => sumField(a, 'lines_processed') - sumField(b, 'lines_processed'),
      render: (_, record) => sumField(record, 'lines_processed'),
    },
    {
      title: t('operator.workforce_stats.items_processed'),
      key: 'items_processed',
      width: 100,
      sorter: (a, b) => sumField(a, 'items_processed') - sumField(b, 'items_processed'),
      render: (_, record) => sumField(record, 'items_processed'),
    },
    {
      title: t('operator.workforce_stats.work_hours'),
      key: 'work_hours',
      width: 100,
      sorter: (a, b) => sumField(a, 'total_work_seconds') - sumField(b, 'total_work_seconds'),
      render: (_, record) => {
        const seconds = sumField(record, 'total_work_seconds');
        return seconds > 0 ? (seconds / 3600).toFixed(1) + 'h' : '-';
      },
    },
    {
      title: 'UPH',
      key: 'uph_items',
      width: 80,
      sorter: (a, b) => avgField(a, 'uph_items') - avgField(b, 'uph_items'),
      render: (_, record) => {
        const val = avgField(record, 'uph_items');
        return val > 0 ? val.toFixed(1) : '-';
      },
    },
    {
      title: t('operator.workforce_stats.actions'),
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <a onClick={() => navigate(`/workforce_stats/operator/${record.operator_id}`)}>
          {t('common.button.detail')}
        </a>
      ),
    },
  ];

  return (
    <div>
      <ListPageHeader
        title={t('operator.workforce_stats.daily_summary_title')}
        breadcrumb={[
          { title: t('operator.workforce_stats.title') },
          { title: t('operator.workforce_stats.daily_summary_title') },
        ]}
      />

      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <RangePicker
          value={dateRange}
          onChange={(val) => {
            if (val) {
              setDateRange(val);
              setSelectedDate(val[1].format('YYYY-MM-DD'));
            }
          }}
          allowClear={false}
        />
        <Select
          placeholder={t('operator.workforce_stats.filter_station')}
          allowClear
          value={selectedStation}
          onChange={setSelectedStation}
          style={{ width: 160 }}
          options={STATION_OPTIONS.map((s) => ({
            value: s,
            label: t(`operator.workforce_stats.stations.${s}`),
          }))}
        />
      </div>

      <Spin spinning={loading}>
        {/* UPH 趨勢折線圖 */}
        {uphTrendData.length > 0 && (
          <Card title={t('operator.workforce_stats.uph_trend')} style={{ marginBottom: 24 }}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={uphTrendData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Legend />
                {activeStations.map((st) => (
                  <Line
                    key={st}
                    type="monotone"
                    dataKey={`uph_${st}`}
                    name={t(`operator.workforce_stats.stations.${st}`) + ' UPH'}
                    stroke={STATION_COLORS[st] || '#999'}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* 每日產出堆疊柱狀圖 */}
        {outputTrendData.length > 0 && (
          <Card title={t('operator.workforce_stats.items_trend')} style={{ marginBottom: 24 }}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={outputTrendData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Legend />
                {activeStations.map((st) => (
                  <Bar
                    key={st}
                    dataKey={`items_${st}`}
                    name={t(`operator.workforce_stats.stations.${st}`)}
                    fill={STATION_COLORS[st] || '#999'}
                    stackId="items"
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* 選定日期的站別統計卡片 */}
        <div style={{ marginBottom: 24 }}>
          <Card
            title={`${selectedDate} ${t('operator.workforce_stats.daily_summary_title')}`}
            extra={
              <DatePicker
                value={dayjs(selectedDate)}
                onChange={(val) => val && setSelectedDate(val.format('YYYY-MM-DD'))}
                allowClear={false}
                disabledDate={(current) =>
                  current < dateRange[0].startOf('day') || current > dateRange[1].endOf('day')
                }
                size="small"
              />
            }
          >
            {renderStationCards()}
          </Card>
        </div>

        {/* 全員績效表格 */}
        <Card title={`${selectedDate} ${t('operator.workforce_stats.operators_overview_title')}`}>
          <Table
            dataSource={operators}
            columns={operatorColumns}
            rowKey="operator_id"
            pagination={false}
            scroll={{ x: 800 }}
            size="small"
          />
        </Card>
      </Spin>
    </div>
  );
};

const sumField = (record, field) =>
  (record.stations || []).reduce((sum, s) => sum + (s[field] || 0), 0);

const avgField = (record, field) => {
  const values = (record.stations || []).map((s) => s[field] || 0).filter((v) => v > 0);
  if (values.length === 0) return 0;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
};

export default DailySummary;
